import React from "react";
import FormControl from "../../inputs/form_control";
import Textarea, { TextareaOverrides, TextareaProps } from "../../inputs/textarea";
import PromptWithSecondaryText from "./shared_components/prompt_with_secondary_text";
import { Overrides } from "baseui/overrides";
import nextId from "react-id-generator";
import { useConstructAriaLabel } from "./use_construct_aria_label";
import { FormControlOverrides } from "baseui/form-control";
import { getFormControlLabel } from "./shared_components/shared_form_control_label";

const TEXT_AREA_NAMESPACE = "textarea-input";

export interface TextareaWrapperProps extends TextareaProps {
  supplementalQuestionPrompt?: string;
  secondaryText?: string;
  label?: string;
  optional: boolean;
  placeholder?: string;
  errorMessage?: string;
}

const textareaOverrides = (
  currentCharacterCount?: number,
  label?: string,
): Overrides<TextareaOverrides> => ({
  Root: {
    style: ({ $theme }) => ({
      marginTop:
        // If we have a character counter OR no label, remove the top margin.
        currentCharacterCount != undefined || !label ? undefined : `-${$theme.sizing.scale500}`,
      maxWidth: "620px",
    }),
  },
});

const TextareaQuestionWrapper = ({
  supplementalQuestionPrompt,
  secondaryText,
  label,
  optional,
  placeholder,
  errorMessage,
  ...textareaProps
}: TextareaWrapperProps) => {
  const { constructAriaLabel } = useConstructAriaLabel();

  const textAreaId = nextId(TEXT_AREA_NAMESPACE);

  const formControlOverrides = (): Overrides<FormControlOverrides> => ({
    Label: {
      style: () => ({
        marginBottom: "0",
      }),
    },
  });

  return (
    <div>
      {supplementalQuestionPrompt && (
        <PromptWithSecondaryText
          supplementalQuestionPrompt={supplementalQuestionPrompt}
          secondaryText={secondaryText}
          optional={optional}
          dataTest={textareaProps?.dataTest}
        />
      )}

      <FormControl
        label={getFormControlLabel(
          supplementalQuestionPrompt,
          label,
          optional,
          secondaryText,
          textareaProps?.dataTest,
        )}
        htmlFor={textAreaId}
        error={errorMessage}
        dataTest={textareaProps?.dataTest}
        overrides={formControlOverrides()}
      >
        <Textarea
          id={textAreaId}
          aria-label={constructAriaLabel(
            supplementalQuestionPrompt,
            secondaryText,
            label,
            optional,
            textareaProps.maxCharacterCount,
          )}
          placeholder={placeholder}
          {...textareaProps}
          error={!!errorMessage}
          overrides={textareaOverrides(textareaProps.currentCharacterCount)}
        />
      </FormControl>
    </div>
  );
};

export default TextareaQuestionWrapper;
